export const guidesHub = {
  path: '/guides',
  images: {
    hero: '/feature-payments.webp',
  },
  title: 'Guides for Stripe-powered restaurant POS',
  description:
    'Short walkthroughs for connecting your Stripe account, understanding payments, and running tips and tax at the table.',
  seoTitle: 'Guides: Stripe & Restaurant POS | Tillio',
  seoDescription:
    'Learn how to connect Stripe to Tillio POS, understand tips and taxes at checkout, and get the most from Terminal payments.',
}

export const guides = [
  {
    slug: 'connect-stripe-account',
    path: '/guides/connect-stripe-account',
    title: 'Connect your Stripe account',
    seoTitle: 'Connect Your Stripe Account to Tillio POS | Guide',
    seoDescription:
      'Authorize Tillio with Stripe Connect OAuth from admin settings. Use your existing Stripe account for Terminal payments—no API keys to copy.',
    images: {
      hero: '/feature-payments.webp',
      card: '/feature-payments.webp',
      section1: '/feature-payments.webp', // Open Tillio admin settings
      section2: '/showcase-payments.webp', // Start Connect with Stripe
      section3: '/category-take-payments.webp', // Authorize Tillio
      section4: '/hardware-terminal.webp', // Pair a reader and take a test payment
    },
    summary:
      'Tillio uses Stripe Connect OAuth so you can link the Stripe account you already have. Card funds stay with Stripe; Tillio provides restaurant POS and Terminal checkout.',
    steps: [
      {
        title: 'Open Tillio admin settings',
        description:
          'Sign in at app.tilliopayment.com with an owner or admin role. Open Settings and find the Stripe payments section.',
      },
      {
        title: 'Start Connect with Stripe',
        description:
          'Choose Connect your Stripe account. You’ll be redirected to Stripe’s secure login—Tillio never sees your Stripe password.',
      },
      {
        title: 'Authorize Tillio',
        description:
          'Review the permissions and confirm. Stripe links your account so Tillio can create Terminal payments on your behalf.',
      },
      {
        title: 'Pair a reader and take a test payment',
        description:
          'Register a Stripe Terminal reader, open a check in Tillio, and run a small test charge. Confirm it appears in your Stripe Dashboard.',
      },
    ],
    faqs: [
      {
        q: 'Do I need a new Stripe account?',
        a: 'No. Connect the account you already use. If you don’t have Stripe yet, you can create one during OAuth—Stripe account creation is free.',
      },
      {
        q: 'Is it safe to connect Stripe?',
        a: 'Yes. Connection uses Stripe’s official OAuth flow. Your bank details stay with Stripe, and you can revoke access from the Stripe Dashboard later.',
      },
      {
        q: 'Where does the money go?',
        a: 'Charges settle to your Stripe balance and payout to your bank on Stripe’s schedule. Tillio does not hold card funds.',
      },
      {
        q: 'What does it cost?',
        a: 'Tillio platform pricing starts at $99/mo. Card payments include a 1.1% Tillio flat rate plus Stripe’s processing rates (stripe.com/pricing). See Pricing for details.',
      },
    ],
    related: ['what-is-stripe', 'tips-gratuity', 'taxes'],
  },
  {
    slug: 'what-is-stripe',
    path: '/guides/what-is-stripe',
    title: 'What is Stripe?',
    seoTitle: 'What Is Stripe? For Restaurant Owners | Tillio Guides',
    seoDescription:
      'Stripe is the payment processor behind online and in-person card charges. Learn how Tillio POS connects your Stripe account for restaurant Terminal payments.',
    images: {
      hero: '/showcase-payments.webp',
      card: '/showcase-payments.webp',
      section1: '/showcase-payments.webp', // Stripe processes the card
      section2: '/feature-analytics.webp', // Your Dashboard is the money view
      section3: '/feature-payments.webp', // Tillio connects for restaurant use
      section4: '/hardware-terminal.webp', // Readers talk to Stripe Terminal
    },
    summary:
      'Stripe is a payments company that lets businesses accept cards, manage payouts, and stay PCI-ready. Tillio is restaurant POS software that connects to your Stripe account for in-person Terminal checkout.',
    steps: [
      {
        title: 'Stripe processes the card',
        description:
          'When a guest pays, Stripe handles authorization, settlement, and much of the compliance burden so card data isn’t stored on your POS tablet.',
      },
      {
        title: 'Your Dashboard is the money view',
        description:
          'Balances, payouts, disputes, and tax forms live in Stripe. Tillio focuses on orders, tables, and the checkout experience on the floor.',
      },
      {
        title: 'Tillio connects for restaurant use',
        description:
          'With Stripe Connect OAuth, Tillio can create Terminal payments on your account while you keep Stripe as the financial backbone.',
      },
      {
        title: 'Readers talk to Stripe Terminal',
        description:
          'Hardware like the S700, M2, WisePOS E, and WisePad 3 is Stripe Terminal gear. Tillio drives the ticket; the reader and Stripe move the funds.',
      },
    ],
    faqs: [
      {
        q: 'Is Tillio a payment processor?',
        a: 'Tillio is restaurant POS software. Card processing runs through Stripe on the account you connect. Platform fee is separate from Stripe’s processing.',
      },
      {
        q: 'Can I keep using Stripe for online sales?',
        a: 'Yes. Connecting Tillio doesn’t replace your Stripe account. Online and in-person history can stay under the same Stripe business.',
      },
      {
        q: 'What readers work with Tillio?',
        a: 'Supported Stripe Terminal readers include Stripe Reader S700/S710, M2, BBPOS WisePOS E, and WisePad 3. See Card readers and Hardware.',
      },
    ],
    related: ['connect-stripe-account', 'tips-gratuity', 'taxes'],
  },
  {
    slug: 'tips-gratuity',
    path: '/guides/tips-gratuity',
    title: 'Tips and gratuity',
    seoTitle: 'Tips & Gratuity with Tillio POS and Stripe | Guide',
    seoDescription:
      'Collect tips at checkout with Tillio POS and Stripe Terminal. Learn how tip prompts, split checks, and your connected Stripe account work together.',
    images: {
      hero: '/feature-pos.webp',
      card: '/feature-pos.webp',
      section1: '/feature-pos.webp', // Close the check in Tillio
      section2: '/hardware-terminal.webp', // Present tip options
      section3: '/showcase-payments.webp', // Capture the full amount on Stripe
      section4: '/feature-analytics.webp', // Reconcile with your team
    },
    summary:
      'Service businesses live on tips. Tillio supports tip workflows at checkout; on supported smart readers, guests can confirm tips on the customer-facing display while the charge settles to your Stripe account.',
    steps: [
      {
        title: 'Close the check in Tillio',
        description:
          'When the table is ready to pay, open the check and start payment. The POS total is the base for tip prompts—no retyping on a separate app.',
      },
      {
        title: 'Present tip options',
        description:
          'Guests choose a tip percentage or custom amount according to your workflow. Smart Terminal readers can show tip UI on the customer screen.',
      },
      {
        title: 'Capture the full amount on Stripe',
        description:
          'The final amount—including tip—processes through Stripe Terminal on your connected account. Review the charge later in the Stripe Dashboard.',
      },
      {
        title: 'Reconcile with your team',
        description:
          'Use Tillio reporting alongside Stripe history so tip totals match what servers earned for the shift.',
      },
    ],
    faqs: [
      {
        q: 'Do tips go to a separate Stripe account?',
        a: 'No. Tips are part of the payment on the Stripe account you connected to Tillio. How you distribute tips to staff is your restaurant policy.',
      },
      {
        q: 'Can guests tip on the reader?',
        a: 'Supported Stripe Terminal smart readers (such as S700 and WisePOS E) offer customer-facing tip prompts. Mobile readers still work with Tillio tip workflows.',
      },
      {
        q: 'Do tips change the card rate?',
        a: 'The 1.1% Tillio flat rate and Stripe’s processing rates apply to the total charged, including tip. See Pricing and stripe.com/pricing for current fees.',
      },
    ],
    related: ['connect-stripe-account', 'taxes', 'what-is-stripe'],
  },
  {
    slug: 'taxes',
    path: '/guides/taxes',
    title: 'Taxes at checkout',
    seoTitle: 'Sales Tax at Checkout with Tillio POS | Guide',
    seoDescription:
      'Configure menu and checkout tax in Tillio POS while card payments settle on your connected Stripe account. Reporting stays clear for restaurant owners.',
    images: {
      hero: '/feature-analytics.webp',
      card: '/feature-analytics.webp',
      section1: '/feature-menu.webp', // Set tax on menus and items
      section2: '/feature-pos.webp', // Show tax on the guest check
      section3: '/hardware-terminal.webp', // Charge the full total via Terminal
      section4: '/feature-analytics.webp', // Report from POS and Stripe
    },
    summary:
      'Restaurants need predictable tax on tickets. Tillio applies tax according to how you configure menus and checkout; the card charge—including tax—settles through Stripe on your connected account.',
    steps: [
      {
        title: 'Set tax on menus and items',
        description:
          'Configure rates and taxable items in Tillio so every ticket calculates consistently across tablets.',
      },
      {
        title: 'Show tax on the guest check',
        description:
          'Guests and servers see tax as part of the open check before the reader is presented—fewer surprises at the table.',
      },
      {
        title: 'Charge the full total via Terminal',
        description:
          'When payment runs, Stripe processes the full amount on your account. Card data remains with Stripe for PCI-ready handling.',
      },
      {
        title: 'Report from POS and Stripe',
        description:
          'Use Tillio analytics for sales by day and category, and Stripe for settlement and payout detail. Keep your accountant’s preferred exports handy.',
      },
    ],
    faqs: [
      {
        q: 'Does Tillio file my sales tax returns?',
        a: 'Tillio helps you calculate and collect tax on tickets. Filing and remittance remain your responsibility (or your accountant’s) under local rules.',
      },
      {
        q: 'Is this the same as Stripe Tax?',
        a: 'Tillio’s checkout tax is configured in the POS for restaurant tickets. Stripe may offer separate tax products for other channels—your Connect setup doesn’t replace POS tax settings.',
      },
      {
        q: 'Where do I see taxed charges?',
        a: 'See the closed check in Tillio and the matching PaymentIntent/charge in your Stripe Dashboard after Terminal capture.',
      },
    ],
    related: ['connect-stripe-account', 'tips-gratuity', 'what-is-stripe'],
  },
]

export function getGuide(slug) {
  return guides.find((g) => g.slug === slug)
}

export function getRelatedGuides(slugs = []) {
  return slugs.map((slug) => getGuide(slug)).filter(Boolean)
}
